import type { Thought } from "@/entities/thoughts/model/types"
import * as m from "@/paraglide/messages"
import { getLocale } from "@/shared/lib/i18n"

interface ThoughtsMonthEntry {
	id: string
	title: string
	description: string
	dateLabel: number
	isNew: boolean
}

export function formatThoughtDate(date: Date | string): string {
	return new Date(date).toLocaleDateString(getLocale(), {
		day: "numeric",
		month: "long",
		year: "numeric",
	})
}

export function compareThoughtsByDate(a: Thought, b: Thought): number {
	const dateA = new Date(a.thoughts_date).getTime()
	const dateB = new Date(b.thoughts_date).getTime()
	if (dateA !== dateB) return dateA - dateB
	return a.id - b.id
}

function formatMonthLabel(date: Date): string {
	const monthFormatter = new Intl.DateTimeFormat(getLocale(), { month: "long" })
	const year = date.getFullYear()
	const month = monthFormatter.format(date)
	const capitalized = month.charAt(0).toUpperCase() + month.slice(1)
	return `${capitalized} ${year}`
}

function mapThoughtToMonthEntry(
	thought: Thought,
	highlightedIds: ReadonlySet<number>,
): ThoughtsMonthEntry {
	return {
		id: String(thought.id),
		title: thought.title || m.commonRecord(),
		description: thought.thoughts ?? "",
		dateLabel: new Date(thought.thoughts_date).getDate(),
		isNew: highlightedIds.has(thought.id),
	}
}

export function groupThoughtsByMonth(
	thoughts: Thought[] | undefined,
	highlightedIds: ReadonlySet<number>,
): Record<string, ThoughtsMonthEntry[]> {
	const groups: Record<string, ThoughtsMonthEntry[]> = {}
	if (!thoughts || thoughts.length === 0) return groups

	const sorted = [...thoughts].sort((a, b) => compareThoughtsByDate(b, a))
	for (const thought of sorted) {
		const monthLabel = formatMonthLabel(new Date(thought.thoughts_date))
		if (!groups[monthLabel]) groups[monthLabel] = []
		groups[monthLabel].push(mapThoughtToMonthEntry(thought, highlightedIds))
	}
	return groups
}
